import React from 'react';
import styled from 'styled-components';
import { StatusChip } from '../abf/components/StatusChip';

/** What the ingestion agent stores on a job about the template it was read with (job.template_validation). */
export interface TemplateValidation {
  lookup?: 'ACTIVE' | 'UNAVAILABLE' | 'NOT_APPLIED' | string;
  status?: 'PENDING' | 'PASS' | 'FAIL' | 'INCOMPLETE' | 'NOT_RUN' | 'ERROR' | string;
  reason?: string | null;
  template?: { id?: number; version?: number; label?: string; documentType?: string } | null;
  errors?: Record<string, Array<{ rule: string; message: string; source?: string }>>;
  labels?: Record<string, string>;
  error_count?: number;
  /** F-PCP-060: fields whose citation the template requires but for which no passage was located. */
  citation_gaps?: string[];
}

const Box = styled.section`
  margin: 0 1.25rem 1rem; padding: 12px 14px; border: 1px solid #36485e; border-radius: 8px; background: #101c2b;
  color: #c5d4e6; font-size: 13px;
  .head { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 14px; }
  strong { color: #edf4ff; }
  ul { margin: 8px 0 0; padding: 0; list-style: none; }
  li { margin: 5px 0; }
  li span { color: #91a5bd; }
  .reason { color: #a8b6ca; margin-top: 6px; }
`;

const SOURCE_WORDS: Record<string, string> = {
  APPENDIX: "from the file's own data",
  MODEL: 'as read by the model',
  NOT_STATED: 'not stated in the document',
};

/**
 * The review page's header line for a templated read: "Read with ABF Servicer Report template v2", and
 * each value that broke one of the template's rules, named in the template's words. Errors do not block
 * approval (decision D-8); the reviewer decides.
 */
const TemplateReadBanner: React.FC<{ validation?: TemplateValidation | null }> = ({ validation }) => {
  if (!validation) return null;
  const template = validation.template;
  if (validation.lookup && validation.lookup !== 'ACTIVE') {
    return (
      <Box aria-label="How this document was read">
        <div className="head"><strong>Read with the built-in instructions</strong></div>
        {validation.reason && <div className="reason">The template was not used: {validation.reason}</div>}
      </Box>
    );
  }
  const errors = Object.entries(validation.errors || {});
  const chip = validation.status === 'PASS'
    ? <StatusChip tone="done" label="Every value passes the template's rules" />
    : validation.status === 'FAIL'
      ? <StatusChip tone="warning" label={`${validation.error_count ?? errors.length} value(s) to check`} />
      : <StatusChip tone="neutral" label={validation.status === 'NOT_RUN' || validation.status === 'ERROR' ? 'Rules not checked' : 'Checking'} />;
  return (
    <Box aria-label="How this document was read">
      <div className="head">
        <strong>Read with the {template?.label || 'document'} template, version {template?.version ?? '?'}</strong>
        {chip}
      </div>
      {validation.reason && validation.status !== 'PASS' && <div className="reason">{validation.reason}</div>}
      {errors.length > 0 && (
        <ul>
          {errors.map(([pointer, list]) => list.map(error => (
            <li key={pointer + error.rule}>
              <strong>{validation.labels?.[pointer] || pointer}</strong>: {error.message}
              {error.source && SOURCE_WORDS[error.source] ? <span> ({SOURCE_WORDS[error.source]})</span> : null}
            </li>
          )))}
        </ul>
      )}
    {Array.isArray(validation.citation_gaps) && validation.citation_gaps.length > 0 && (
        <p className="reason">No passage was located for {validation.citation_gaps.length} field(s) the template says must cite the document: {validation.citation_gaps.join(', ')}. Point each at its passage before approving.</p>
      )}
      </Box>
  );
};

export default TemplateReadBanner;
